var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateWrapper = (obj, member, setter, getter) => ({
  set _(value) {
    __privateSet(obj, member, value, setter);
  },
  get _() {
    return __privateGet(obj, member, getter);
  }
});
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};

// src/extenders/custom-reviews.js
var _MOBILE_BREAKPOINT, _REVIEWS_PER_PAGE, _MIN_SWIPE_DISTANCE, _STAR_COLOR_FILLED, _STAR_COLOR_EMPTY, _BACKGROUND_COLOR, _reviewUrl, _sliderContainer, _productId, _allReviews, _currentPage, _totalPages, _currentSlide, _averageScore, _isLoading, _abortController, _averageScoreElement, _averageStarsElement, _averageProductScore, _averageProductStarsElement, _averageProductScoreElement, _reviewUrlElement, _initializeElements, initializeElements_fn, _getProductIdFromDOM, getProductIdFromDOM_fn, _init, init_fn, _setLoadingState, setLoadingState_fn, _fetchReviews, fetchReviews_fn, _displayAverageScore, displayAverageScore_fn, _hideAverageScore, hideAverageScore_fn, _showAverageScore, showAverageScore_fn, _updateAverageScoreDisplay, updateAverageScoreDisplay_fn, _setupAverageScoreClickHandler, setupAverageScoreClickHandler_fn, _formatDate, formatDate_fn, _displayReviews, displayReviews_fn, _toggleReviewUrlVisibility, toggleReviewUrlVisibility_fn, _createReviewSlide, createReviewSlide_fn, _escapeHtml, escapeHtml_fn, _createStarRating, createStarRating_fn, _createFullStar, createFullStar_fn, _createHalfStar, createHalfStar_fn, _createEmptyStar, createEmptyStar_fn, _updateSliderPosition, updateSliderPosition_fn, _injectStructuredData, injectStructuredData_fn, _slide, slide_fn, _shouldLoadMoreReviews, shouldLoadMoreReviews_fn, _loadMoreReviews, loadMoreReviews_fn, _loadPreviousReviews, loadPreviousReviews_fn, _clampCurrentSlide, clampCurrentSlide_fn, _attachEventListeners, attachEventListeners_fn, _handleError, handleError_fn;
var _ReviewSlider = class _ReviewSlider {
  /**
   * @param {string} productId - Optional product ID
   * @param {Object} options - Configuration options
   */
  constructor(productId, options = {}) {
    /**
     * Initialize and cache DOM elements
     * @private
     */
    __privateAdd(this, _initializeElements);
    /**
     * Get product ID from DOM
     * @private
     * @returns {string|null}
     */
    __privateAdd(this, _getProductIdFromDOM);
    /**
     * Initialize the component
     * @private
     */
    __privateAdd(this, _init);
    /**
     * Set loading state
     * @private
     * @param {boolean} loading
     */
    __privateAdd(this, _setLoadingState);
    /**
     * Fetch reviews from API
     * @private
     * @returns {Promise<Array>}
     */
    __privateAdd(this, _fetchReviews);
    /**
     * Display average score
     * @private
     */
    __privateAdd(this, _displayAverageScore);
    /**
     * Hide average score elements
     * @private
     */
    __privateAdd(this, _hideAverageScore);
    /**
     * Show average score elements
     * @private
     */
    __privateAdd(this, _showAverageScore);
    /**
     * Update average score display
     * @private
     */
    __privateAdd(this, _updateAverageScoreDisplay);
    /**
     * Setup click handler for average score
     * @private
     */
    __privateAdd(this, _setupAverageScoreClickHandler);
    /**
     * Format date string
     * @private
     * @param {string} dateString
     * @returns {string}
     */
    __privateAdd(this, _formatDate);
    /**
     * Display reviews in slider
     * @private
     * @param {Array} reviews
     */
    __privateAdd(this, _displayReviews);
    /**
     * Toggle review URL visibility
     * @private
     * @param {boolean} visible
     */
    __privateAdd(this, _toggleReviewUrlVisibility);
    /**
     * Create HTML for a review slide
     * @private
     * @param {Object} review
     * @returns {string}
     */
    __privateAdd(this, _createReviewSlide);
    /**
     * Escape HTML to prevent XSS
     * @private
     * @param {string} text
     * @returns {string}
     */
    __privateAdd(this, _escapeHtml);
    /**
     * Create star rating HTML
     * @private
     * @param {number} score
     * @returns {string}
     */
    __privateAdd(this, _createStarRating);
    /**
     * Create full star SVG
     * @private
     * @returns {string}
     */
    __privateAdd(this, _createFullStar);
    /**
     * Create half star SVG
     * @private
     * @returns {string}
     */
    __privateAdd(this, _createHalfStar);
    /**
     * Create empty star SVG
     * @private
     * @returns {string}
     */
    __privateAdd(this, _createEmptyStar);
    /**
     * Update slider position
     * @private
     */
    __privateAdd(this, _updateSliderPosition);
    /**
     * Inject structured data for SEO
     * @private
     */
    __privateAdd(this, _injectStructuredData);
    /**
     * Handle slide navigation
     * @private
     * @param {number} direction
     */
    __privateAdd(this, _slide);
    /**
     * Check if more reviews should be loaded
     * @private
     * @param {boolean} isMobile
     * @param {number} slideCount
     * @returns {boolean}
     */
    __privateAdd(this, _shouldLoadMoreReviews);
    /**
     * Load more reviews
     * @private
     */
    __privateAdd(this, _loadMoreReviews);
    /**
     * Load previous reviews
     * @private
     */
    __privateAdd(this, _loadPreviousReviews);
    /**
     * Clamp current slide within bounds
     * @private
     * @param {number} maxSlides
     */
    __privateAdd(this, _clampCurrentSlide);
    /**
     * Attach event listeners
     * @private
     */
    __privateAdd(this, _attachEventListeners);
    /**
     * Handle errors
     * @private
     * @param {string} message
     */
    __privateAdd(this, _handleError);
    // Private fields
    __privateAdd(this, _reviewUrl, void 0);
    __privateAdd(this, _sliderContainer, void 0);
    __privateAdd(this, _productId, void 0);
    __privateAdd(this, _allReviews, []);
    __privateAdd(this, _currentPage, 1);
    __privateAdd(this, _totalPages, 1);
    __privateAdd(this, _currentSlide, 0);
    __privateAdd(this, _averageScore, 0);
    __privateAdd(this, _isLoading, false);
    __privateAdd(this, _abortController, null);
    // Cached DOM elements
    __privateAdd(this, _averageScoreElement, void 0);
    __privateAdd(this, _averageStarsElement, void 0);
    __privateAdd(this, _averageProductScore, void 0);
    __privateAdd(this, _averageProductStarsElement, void 0);
    __privateAdd(this, _averageProductScoreElement, void 0);
    __privateAdd(this, _reviewUrlElement, void 0);
    var _a;
    __privateMethod(this, _initializeElements, initializeElements_fn).call(this);
    __privateSet(this, _reviewUrl, (_a = __privateGet(this, _reviewUrlElement)) == null ? void 0 : _a.getAttribute("data-review-url"));
    if (!__privateGet(this, _reviewUrl) || !__privateGet(this, _sliderContainer)) {
      return;
    }
    __privateSet(this, _productId, productId || __privateMethod(this, _getProductIdFromDOM, getProductIdFromDOM_fn).call(this));
    __privateMethod(this, _init, init_fn).call(this);
  }
};
_MOBILE_BREAKPOINT = new WeakMap();
_REVIEWS_PER_PAGE = new WeakMap();
_MIN_SWIPE_DISTANCE = new WeakMap();
_STAR_COLOR_FILLED = new WeakMap();
_STAR_COLOR_EMPTY = new WeakMap();
_BACKGROUND_COLOR = new WeakMap();
_reviewUrl = new WeakMap();
_sliderContainer = new WeakMap();
_productId = new WeakMap();
_allReviews = new WeakMap();
_currentPage = new WeakMap();
_totalPages = new WeakMap();
_currentSlide = new WeakMap();
_averageScore = new WeakMap();
_isLoading = new WeakMap();
_abortController = new WeakMap();
_averageScoreElement = new WeakMap();
_averageStarsElement = new WeakMap();
_averageProductScore = new WeakMap();
_averageProductStarsElement = new WeakMap();
_averageProductScoreElement = new WeakMap();
_reviewUrlElement = new WeakMap();
_initializeElements = new WeakSet();
initializeElements_fn = function() {
  __privateSet(this, _reviewUrlElement, document.querySelector("[data-review-url]"));
  __privateSet(this, _sliderContainer, document.getElementById("sliderContainer"));
  __privateSet(this, _averageScoreElement, document.querySelector("[data-average-score]"));
  __privateSet(this, _averageStarsElement, document.querySelector("[data-average-stars]"));
  __privateSet(this, _averageProductScore, document.querySelector("[data-product-score]"));
  __privateSet(this, _averageProductStarsElement, document.querySelector("[data-product-average-stars]"));
  __privateSet(this, _averageProductScoreElement, document.querySelector("[data-product-average-score]"));
};
_getProductIdFromDOM = new WeakSet();
getProductIdFromDOM_fn = function() {
  var _a;
  return ((_a = document.querySelector("[data-product-id]")) == null ? void 0 : _a.getAttribute("data-product-id")) || null;
};
_init = new WeakSet();
init_fn = async function() {
  try {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, true);
    const reviews = await __privateMethod(this, _fetchReviews, fetchReviews_fn).call(this);
    __privateMethod(this, _displayReviews, displayReviews_fn).call(this, reviews);
    if (__privateGet(this, _averageScoreElement)) {
      __privateMethod(this, _displayAverageScore, displayAverageScore_fn).call(this);
    }
    __privateMethod(this, _injectStructuredData, injectStructuredData_fn).call(this);
    __privateMethod(this, _attachEventListeners, attachEventListeners_fn).call(this);
  } catch (error) {
    __privateMethod(this, _handleError, handleError_fn).call(this, "Failed to load reviews");
  } finally {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, false);
  }
};
_setLoadingState = new WeakSet();
setLoadingState_fn = function(loading) {
  __privateSet(this, _isLoading, loading);
  if (__privateGet(this, _sliderContainer)) {
    __privateGet(this, _sliderContainer).classList.toggle("loading", loading);
  }
};
_fetchReviews = new WeakSet();
fetchReviews_fn = async function() {
  var _a;
  if (__privateGet(this, _abortController)) {
    __privateGet(this, _abortController).abort();
  }
  __privateSet(this, _abortController, new AbortController());
  try {
    const response = await fetch(__privateGet(this, _reviewUrl), {
      signal: __privateGet(this, _abortController).signal
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!((_a = data == null ? void 0 : data.response) == null ? void 0 : _a.reviews)) {
      throw new Error("Invalid response format");
    }
    __privateSet(this, _allReviews, data.response.reviews);
    __privateSet(this, _totalPages, Math.ceil(data.response.pagination.total / __privateGet(_ReviewSlider, _REVIEWS_PER_PAGE)));
    __privateSet(this, _averageScore, data.response.bottomline.average_score);
    return __privateGet(this, _allReviews);
  } catch (error) {
    if (error.name === "AbortError") {
      return [];
    }
    throw error;
  }
};
_displayAverageScore = new WeakSet();
displayAverageScore_fn = function() {
  if (!__privateGet(this, _allReviews).length) {
    __privateMethod(this, _hideAverageScore, hideAverageScore_fn).call(this);
    return;
  }
  __privateMethod(this, _showAverageScore, showAverageScore_fn).call(this);
  __privateMethod(this, _updateAverageScoreDisplay, updateAverageScoreDisplay_fn).call(this);
  __privateMethod(this, _setupAverageScoreClickHandler, setupAverageScoreClickHandler_fn).call(this);
};
_hideAverageScore = new WeakSet();
hideAverageScore_fn = function() {
  var _a;
  (_a = __privateGet(this, _averageScoreElement)) == null ? void 0 : _a.classList.add("hidden");
};
_showAverageScore = new WeakSet();
showAverageScore_fn = function() {
  var _a;
  (_a = __privateGet(this, _averageScoreElement)) == null ? void 0 : _a.classList.remove("hidden");
};
_updateAverageScoreDisplay = new WeakSet();
updateAverageScoreDisplay_fn = function() {
  const score = __privateGet(this, _averageScore).toFixed(1);
  __privateGet(this, _averageScoreElement).textContent = score;
  const starHtml = __privateMethod(this, _createStarRating, createStarRating_fn).call(this, __privateGet(this, _averageScore));
  __privateGet(this, _averageStarsElement).innerHTML = starHtml;
  if (__privateGet(this, _averageProductStarsElement)) {
    __privateGet(this, _averageProductStarsElement).innerHTML = starHtml;
    __privateGet(this, _averageProductScoreElement).textContent = score;
  }
};
_setupAverageScoreClickHandler = new WeakSet();
setupAverageScoreClickHandler_fn = function() {
  if (!__privateGet(this, _averageProductScore))
    return;
  __privateGet(this, _averageProductScore).addEventListener("click", () => {
    const reviewSection = document.querySelector(".custom-review-section");
    const headerGroups = document.querySelectorAll(".shopify-section-group-header-group");
    if (reviewSection && headerGroups.length) {
      const offset = Array.from(headerGroups).reduce((total, group) => total + group.offsetHeight, 0);
      window.scrollTo({
        top: reviewSection.offsetTop - offset,
        behavior: "smooth"
      });
    }
  });
};
_formatDate = new WeakSet();
formatDate_fn = function(dateString) {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
  });
};
_displayReviews = new WeakSet();
displayReviews_fn = function(reviews) {
  const html = reviews.length ? reviews.map((review) => __privateMethod(this, _createReviewSlide, createReviewSlide_fn).call(this, review)).join("") : "<p>Er zijn nog geen reviews voor dit product.</p>";
  __privateGet(this, _sliderContainer).innerHTML = html;
  __privateMethod(this, _toggleReviewUrlVisibility, toggleReviewUrlVisibility_fn).call(this, reviews.length > 0);
  __privateSet(this, _currentSlide, 0);
  __privateMethod(this, _updateSliderPosition, updateSliderPosition_fn).call(this);
};
_toggleReviewUrlVisibility = new WeakSet();
toggleReviewUrlVisibility_fn = function(visible) {
  __privateGet(this, _reviewUrlElement).classList.toggle("hidden", !visible);
};
_createReviewSlide = new WeakSet();
createReviewSlide_fn = function(review) {
  var _a;
  const { display_name: displayName } = review.user;
  const starRating = __privateMethod(this, _createStarRating, createStarRating_fn).call(this, review.score);
  const createdAtFormatted = __privateMethod(this, _formatDate, formatDate_fn).call(this, review.created_at);
  const reviewComment = (_a = review.comment) == null ? void 0 : _a.content;
  return `
      <div class="review-slide px-1 min-w-full lg:min-w-[calc(100%_/_3)] lg:max-w-[calc(100%_/_3)]"
           data-user-display-name="${__privateMethod(this, _escapeHtml, escapeHtml_fn).call(this, displayName)}"
           data-product-id-name="${review.id}">
        <div class="px-6 py-5 bg-[${__privateGet(_ReviewSlider, _BACKGROUND_COLOR)}] flex flex-col h-full">
          <div class="flex justify-between items-center">
            <div class="flex gap-1 items-center star-rating">
              ${starRating}
              <span class="text-label-s">${review.score}</span>
            </div>
            <p class="text-label-s">${__privateMethod(this, _escapeHtml, escapeHtml_fn).call(this, displayName)}</p>
          </div>
          <span class="my-2 text-label-m">${__privateMethod(this, _escapeHtml, escapeHtml_fn).call(this, review.title)}</span>
          <p class="text-body-2xs text-light-gray">${__privateMethod(this, _escapeHtml, escapeHtml_fn).call(this, review.content)}</p>
          ${reviewComment ? `
            <div class="p-2 mt-2 bg-white text-body-2xs text-light-gray">
              <p class="text-[9px] italic">${__privateMethod(this, _escapeHtml, escapeHtml_fn).call(this, reviewComment)}</p>
            </div>
          ` : ""}
          <p class="mt-auto text-right text-label-s text-light-gray">${createdAtFormatted}</p>
        </div>
      </div>
    `;
};
_escapeHtml = new WeakSet();
escapeHtml_fn = function(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};
_createStarRating = new WeakSet();
createStarRating_fn = function(score) {
  let stars = "";
  for (let i = 1; i <= 5; i++) {
    if (score >= i) {
      stars += __privateMethod(this, _createFullStar, createFullStar_fn).call(this);
    } else if (score >= i - 0.5) {
      stars += __privateMethod(this, _createHalfStar, createHalfStar_fn).call(this);
    } else {
      stars += __privateMethod(this, _createEmptyStar, createEmptyStar_fn).call(this);
    }
  }
  return stars;
};
_createFullStar = new WeakSet();
createFullStar_fn = function() {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${__privateGet(_ReviewSlider, _STAR_COLOR_FILLED)}" width="12" height="12">
        <path d="M12 .587l3.668 7.431L24 9.335l-6 5.831 1.416 8.332L12 18.902l-7.416 4.596L6 15.166l-6-5.83 8.332-1.318z"/>
      </svg>`;
};
_createHalfStar = new WeakSet();
createHalfStar_fn = function() {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${__privateGet(_ReviewSlider, _STAR_COLOR_FILLED)}" width="12" height="12">
        <defs>
          <linearGradient id="halfGrad">
            <stop offset="50%" stop-color="${__privateGet(_ReviewSlider, _STAR_COLOR_FILLED)}"/>
            <stop offset="50%" stop-color="#E2E2E2"/>
          </linearGradient>
        </defs>
        <path d="M12 .587l3.668 7.431L24 9.335l-6 5.831 1.416 8.332L12 18.902l-7.416 4.596L6 15.166l-6-5.83 8.332-1.318z" fill="url(#halfGrad)"/>
      </svg>`;
};
_createEmptyStar = new WeakSet();
createEmptyStar_fn = function() {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${__privateGet(_ReviewSlider, _STAR_COLOR_EMPTY)}" stroke="none" width="12" height="12">
        <path d="M12 .587l3.668 7.431L24 9.335l-6 5.831 1.416 8.332L12 18.902l-7.416 4.596L6 15.166l-6-5.83 8.332-1.318z"/>
      </svg>`;
};
_updateSliderPosition = new WeakSet();
updateSliderPosition_fn = function() {
  const isMobile = window.innerWidth < __privateGet(_ReviewSlider, _MOBILE_BREAKPOINT);
  const slideWidth = isMobile ? 100 : 100 / 3;
  __privateGet(this, _sliderContainer).style.transform = `translateX(-${__privateGet(this, _currentSlide) * slideWidth}%)`;
};
_injectStructuredData = new WeakSet();
injectStructuredData_fn = function() {
  var _a;
  if (!__privateGet(this, _allReviews) || __privateGet(this, _allReviews).length === 0) {
    return;
  }
  const existingScript = document.getElementById("yotpo-structured-data");
  if (existingScript) {
    existingScript.remove();
  }
  const productName = ((_a = document.querySelector("h1")) == null ? void 0 : _a.textContent.trim()) || "Product";
  const reviews = __privateGet(this, _allReviews).map((review) => ({
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.score,
      "bestRating": "5"
    },
    "author": {
      "@type": "Person",
      "name": review.user.display_name
    },
    "datePublished": review.created_at,
    "reviewBody": review.content,
    "name": review.title
  }));
  let existingProductSchema = null;
  let existingProductScript = null;
  const allSchemaScripts = document.querySelectorAll('script[type="application/ld+json"]');
  for (const script2 of allSchemaScripts) {
    try {
      const data = JSON.parse(script2.textContent);
      if (data && data["@type"] === "Product") {
        existingProductSchema = data;
        existingProductScript = script2;
        break;
      }
    } catch (e) {
      continue;
    }
  }
  if (existingProductScript) {
    existingProductScript.remove();
  }
  let structuredData;
  if (existingProductSchema) {
    structuredData = {
      ...existingProductSchema,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": __privateGet(this, _averageScore).toFixed(1),
        "reviewCount": __privateGet(this, _allReviews).length,
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": reviews
    };
  } else {
    structuredData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": productName,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": __privateGet(this, _averageScore).toFixed(1),
        "reviewCount": __privateGet(this, _allReviews).length,
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": reviews
    };
  }
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "yotpo-structured-data";
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};
_slide = new WeakSet();
slide_fn = async function(direction) {
  if (__privateGet(this, _isLoading))
    return;
  const slides = document.querySelectorAll(".review-slide");
  const isMobile = window.innerWidth < __privateGet(_ReviewSlider, _MOBILE_BREAKPOINT);
  __privateSet(this, _currentSlide, __privateGet(this, _currentSlide) + direction);
  if (__privateMethod(this, _shouldLoadMoreReviews, shouldLoadMoreReviews_fn).call(this, isMobile, slides.length)) {
    await __privateMethod(this, _loadMoreReviews, loadMoreReviews_fn).call(this);
  } else if (__privateGet(this, _currentSlide) < 0 && __privateGet(this, _currentPage) > 1) {
    await __privateMethod(this, _loadPreviousReviews, loadPreviousReviews_fn).call(this);
  } else {
    __privateMethod(this, _clampCurrentSlide, clampCurrentSlide_fn).call(this, slides.length);
  }
  __privateMethod(this, _updateSliderPosition, updateSliderPosition_fn).call(this);
};
_shouldLoadMoreReviews = new WeakSet();
shouldLoadMoreReviews_fn = function(isMobile, slideCount) {
  return isMobile && __privateGet(this, _currentSlide) >= slideCount && __privateGet(this, _currentPage) < __privateGet(this, _totalPages) || !isMobile && __privateGet(this, _currentSlide) >= slideCount / 3 && __privateGet(this, _currentPage) < __privateGet(this, _totalPages);
};
_loadMoreReviews = new WeakSet();
loadMoreReviews_fn = async function() {
  try {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, true);
    __privateWrapper(this, _currentPage)._++;
    const reviews = await __privateMethod(this, _fetchReviews, fetchReviews_fn).call(this);
    __privateMethod(this, _displayReviews, displayReviews_fn).call(this, reviews);
  } catch (e) {
    __privateSet(this, _currentSlide, Math.max(0, __privateGet(this, _currentSlide) - 1));
  } finally {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, false);
  }
};
_loadPreviousReviews = new WeakSet();
loadPreviousReviews_fn = async function() {
  try {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, true);
    __privateWrapper(this, _currentPage)._--;
    const reviews = await __privateMethod(this, _fetchReviews, fetchReviews_fn).call(this);
    __privateMethod(this, _displayReviews, displayReviews_fn).call(this, reviews);
    __privateSet(this, _currentSlide, __privateGet(this, _allReviews).length - 1);
  } catch (e) {
    __privateSet(this, _currentSlide, 0);
  } finally {
    __privateMethod(this, _setLoadingState, setLoadingState_fn).call(this, false);
  }
};
_clampCurrentSlide = new WeakSet();
clampCurrentSlide_fn = function(maxSlides) {
  __privateSet(this, _currentSlide, Math.max(0, Math.min(__privateGet(this, _currentSlide), maxSlides - 1)));
};
_attachEventListeners = new WeakSet();
attachEventListeners_fn = function() {
  var _a, _b;
  (_a = document.querySelector(".prev")) == null ? void 0 : _a.addEventListener("click", () => __privateMethod(this, _slide, slide_fn).call(this, -1));
  (_b = document.querySelector(".next")) == null ? void 0 : _b.addEventListener("click", () => __privateMethod(this, _slide, slide_fn).call(this, 1));
  let touchStartX = null;
  __privateGet(this, _sliderContainer).addEventListener("touchstart", (e) => {
    touchStartX = e.changedTouches[0].clientX;
  });
  __privateGet(this, _sliderContainer).addEventListener("touchend", (e) => {
    if (!touchStartX)
      return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX - touchEndX;
    if (Math.abs(diffX) > __privateGet(_ReviewSlider, _MIN_SWIPE_DISTANCE)) {
      __privateMethod(this, _slide, slide_fn).call(this, diffX > 0 ? 1 : -1);
    }
  });
  window.addEventListener("resize", () => __privateMethod(this, _updateSliderPosition, updateSliderPosition_fn).call(this));
};
_handleError = new WeakSet();
handleError_fn = function(message) {
  if (__privateGet(this, _sliderContainer)) {
    __privateGet(this, _sliderContainer).innerHTML = `<p class="error">${message}</p>`;
  }
};
// Constants
__privateAdd(_ReviewSlider, _MOBILE_BREAKPOINT, 1024);
__privateAdd(_ReviewSlider, _REVIEWS_PER_PAGE, 50);
__privateAdd(_ReviewSlider, _MIN_SWIPE_DISTANCE, 50);
__privateAdd(_ReviewSlider, _STAR_COLOR_FILLED, "#232222");
__privateAdd(_ReviewSlider, _STAR_COLOR_EMPTY, "#bdbdbd");
__privateAdd(_ReviewSlider, _BACKGROUND_COLOR, "#F6F6F6");
var ReviewSlider = _ReviewSlider;
document.addEventListener("DOMContentLoaded", () => {
  new ReviewSlider();
});
